import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import { notify } from '../../utils/notifications';
import {
  Plus, Search, Eye, Edit, Trash2, UserCircle, Phone, Mail,
  CheckCircle, XCircle, Shield, Calendar, MapPin, Bus, X, Key
} from 'lucide-react';

const Autisti = () => {
  const { utente } = authStore();
  const modificabile = puoModificare(utente);
  const navigate = useNavigate();
  const [autisti, setAutisti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroAttivo, setFiltroAttivo] = useState('TUTTI');
  const [showModal, setShowModal] = useState(false);
  const [editingAutista, setEditingAutista] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password_conferma: '',
    first_name: '',
    last_name: '',
    telefono: '',
    is_active: true,
    richiede_2fa: false,
    note_interne: ''
  });

  useEffect(() => {
    caricaDati();
  }, []);

  const caricaDati = async () => {
    try {
      setLoading(true);
      
      // Il filtro lo fa il server: scaricare tutti gli utenti e scremarli
      // qui significherebbe perdere gli autisti oltre la prima pagina.
      const autistiData = await getAll('/autenticazione/utenti/', { ruolo: 'AUTISTA' });
      setAutisti(autistiData);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
      notify.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (autista = null) => {
    if (autista) {
      setEditingAutista(autista);
      setFormData({
        username: autista.username,
        email: autista.email,
        password: '', // Non mostriamo la password per sicurezza
        password_conferma: '',
        first_name: autista.first_name || '',
        last_name: autista.last_name || '',
        telefono: autista.telefono || '',
        is_active: autista.is_active,
        richiede_2fa: autista.richiede_2fa,
        note_interne: autista.note_interne || ''
      });
    } else {
      setEditingAutista(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        password_conferma: '',
        first_name: '',
        last_name: '',
        telefono: '',
        is_active: true,
        richiede_2fa: false,
        note_interne: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAutista(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione password conferma per nuovi autisti
    if (!editingAutista && formData.password !== formData.password_conferma) {
      notify.error('Le password non corrispondono');
      return;
    }
    
    const toastId = notify.loading(editingAutista ? 'Aggiornamento...' : 'Creazione...');
    
    try {
      const payload = { ...formData, ruolo: 'AUTISTA' };
      
      // Se in modifica e password vuota, non inviarla
      if (editingAutista && !formData.password) {
        delete payload.password;
        delete payload.password_conferma;
      }
      
      if (editingAutista) {
        await api.put(`/autenticazione/utenti/${editingAutista.id}/`, payload);
        notify.update(toastId, { type: 'success', render: 'Autista aggiornato con successo!' });
      } else {
        await api.post('/autenticazione/utenti/', payload);
        notify.update(toastId, { type: 'success', render: 'Autista creato con successo!' });
      }
      await caricaDati();
      handleCloseModal();
    } catch (error) {
      console.error('Errore salvataggio autista:', error);
      const errorMsg = error.response?.data?.username?.[0] || 
                       error.response?.data?.email?.[0] || 
                       error.response?.data?.password?.[0] ||
                       error.response?.data?.detail || 
                       'Errore nel salvataggio';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo autista?')) return;
    
    const toastId = notify.loading('Eliminazione...');
    
    try {
      await api.delete(`/autenticazione/utenti/${id}/`);
      notify.update(toastId, { type: 'success', render: 'Autista eliminato con successo!' });
      await caricaDati();
    } catch (error) {
      console.error('Errore eliminazione autista:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'eliminazione dell\'autista' });
    }
  };

  const handleToggleAttivo = async (id, currentStatus) => {
    const toastId = notify.loading(currentStatus ? 'Disattivazione...' : 'Attivazione...');
    
    try {
      await api.post(`/autenticazione/utenti/${id}/attiva_disattiva/`);
      notify.update(toastId, { 
        type: 'success', 
        render: currentStatus ? 'Autista disattivato!' : 'Autista attivato!' 
      });
      await caricaDati();
    } catch (error) {
      console.error('Errore toggle attivo:', error);
      notify.update(toastId, { type: 'error', render: 'Errore nell\'operazione' });
    }
  };

  const handleResetPassword = async (id, username) => {
    const newPassword = prompt(`Inserisci la nuova password per ${username}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 4) {
      notify.error('La password deve essere di almeno 4 caratteri');
      return;
    }
    
    const toastId = notify.loading('Reset password...');
    
    try {
      await api.post(`/autenticazione/utenti/${id}/reset_password/`, { nuova_password: newPassword });
      notify.update(toastId, { type: 'success', render: 'Password reimpostata con successo!' });
    } catch (error) {
      console.error('Errore reset password:', error);
      const errorMsg = error.response?.data?.errore || 'Errore nel reset della password';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const filteredAutisti = autisti.filter(autista => {
    const matchSearch = 
      autista.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      autista.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      autista.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      autista.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      autista.telefono?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchAttivo = 
      filtroAttivo === 'TUTTI' || 
      (filtroAttivo === 'ATTIVI' && autista.is_active) ||
      (filtroAttivo === 'INATTIVI' && !autista.is_active);
    
    return matchSearch && matchAttivo;
  });

  // Calcola statistiche
  const stats = {
    totale: autisti.length,
    attivi: autisti.filter(a => a.is_active).length,
    inattivi: autisti.filter(a => !a.is_active).length,
    con2fa: autisti.filter(a => a.richiede_2fa).length,
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Autisti</h1>
            <p className="text-sm text-gray-500 mt-0.5">Gestisci gli autisti e i loro accessi</p>
          </div>
          {modificabile && (
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition shadow-sm font-semibold"
          >
            <Plus className="w-4 h-4" />
            Nuovo Autista
          </button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Totale Autisti</p>
              <UserCircle className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totale}</p>
            <p className="text-gray-400 text-xs mt-1">nel sistema</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Attivi</p>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.attivi}</p>
            <p className="text-gray-400 text-xs mt-1">autisti operativi</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Inattivi</p>
              <XCircle className="w-4 h-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.inattivi}</p>
            <p className="text-gray-400 text-xs mt-1">non operativi</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500 text-sm">Con 2FA</p>
              <Shield className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.con2fa}</p>
            <p className="text-gray-400 text-xs mt-1">sicurezza extra</p>
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-5">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca per nome, cognome, username, email o telefono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={filtroAttivo}
              onChange={(e) => setFiltroAttivo(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="TUTTI">Tutti</option>
              <option value="ATTIVI">Solo Attivi</option>
              <option value="INATTIVI">Solo Inattivi</option>
            </select>
          </div>
        </div>

        {/* Tabella Autisti */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {filteredAutisti.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <UserCircle className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nessun autista trovato</h3>
              <p className="text-gray-600 mb-5">
                {searchTerm || filtroAttivo !== 'TUTTI' 
                  ? 'Prova con termini di ricerca diversi' 
                  : 'Inizia aggiungendo il primo autista'}
              </p>
              {!searchTerm && filtroAttivo === 'TUTTI' && (
                <button
                  onClick={() => handleOpenModal()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Autista
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Autista
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contatti
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accesso
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAutisti.map((autista) => (
                    <tr key={autista.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                            autista.is_active ? 'bg-blue-600' : 'bg-gray-400'
                          }`}>
                            {autista.first_name ? autista.first_name.charAt(0).toUpperCase() : autista.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {autista.first_name && autista.last_name 
                                ? `${autista.first_name} ${autista.last_name}`
                                : autista.username
                              }
                            </div>
                            <div className="text-xs text-gray-500">@{autista.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {autista.email && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {autista.email}
                            </div>
                          )}
                          {autista.telefono && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-4 h-4 text-gray-400" />
                              {autista.telefono}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {new Date(autista.data_creazione).toLocaleDateString('it-IT')}
                          </div>
                          {autista.ultimo_accesso_ip && (
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <MapPin className="w-3 h-3 text-gray-400" />
                              {autista.ultimo_accesso_ip}
                            </div>
                          )}
                          {autista.richiede_2fa && (
                            <div className="flex items-center gap-1 text-xs text-purple-600">
                              <Shield className="w-3 h-3" />
                              2FA attivo
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleAttivo(autista.id, autista.is_active)}
                          className={`inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full font-semibold transition ${
                            autista.is_active 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {autista.is_active ? (
                            <>
                              <CheckCircle className="w-3 h-3" />
                              Attivo
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3" />
                              Inattivo
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/autisti/${autista.id}`)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="Visualizza dettagli"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(autista.id, autista.username)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition"
                            title="Reset password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenModal(autista)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(autista.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contatore */}
        {filteredAutisti.length > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-center">
            Totale: {filteredAutisti.length} {filteredAutisti.length === 1 ? 'autista' : 'autisti'}
          </div>
        )}
      </div>

      {/* Modal Creazione/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingAutista ? 'Modifica Autista' : 'Nuovo Autista'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome *</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Mario"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cognome *</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Rossi"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Username *</label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  disabled={!!editingAutista}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="mario.rossi"
                />
                {editingAutista && (
                  <p className="text-xs text-gray-500 mt-1">Username non modificabile dopo la creazione</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="mario.rossi@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+39 123 456 7890"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password {editingAutista ? '(lascia vuoto per non modificare)' : '*'}
                  </label>
                  <input
                    type="password"
                    required={!editingAutista}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>

                {!editingAutista && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Conferma Password *</label>
                    <input
                      type="password"
                      required
                      value={formData.password_conferma}
                      onChange={(e) => setFormData({ ...formData, password_conferma: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="••••••••"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Attivo</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.richiede_2fa}
                    onChange={(e) => setFormData({ ...formData, richiede_2fa: e.target.checked })}
                    className="w-4 h-4 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    Richiede 2FA
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Note Interne</label>
                <textarea
                  value={formData.note_interne}
                  onChange={(e) => setFormData({ ...formData, note_interne: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Note visibili solo agli amministratori..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold"
                >
                  {editingAutista ? 'Salva Modifiche' : 'Crea Autista'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Autisti;
